import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * ONNI 24.08.2026 (Auftrag Paavo): Die grosse Menueleiste kennt ZWEI Bedienarten.
 *
 * - Maus-Desktop (feiner Zeiger, echtes Hover): Flyout oeffnet beim Drueberfahren
 *   und schliesst beim Wegfahren. Ein Klick auf Produkte, SETs oder Ueber uns
 *   folgt ganz normal dem Link auf die jeweilige Seite.
 * - Tablet mit derselben Leiste (iPad, grober Zeiger, kein Hover): dort gibt es
 *   kein Drueberfahren, deshalb oeffnet und schliesst der Tipp das Flyout und
 *   folgt NICHT dem Link.
 *
 * Vorher (01.08. bis 24.08.) galt der Klick-Weg fuer beide, auf Maus-Desktop
 * kostete das jeden Menuepunkt einen zweiten Klick.
 */
const ONNI_GROSSE_LEISTE = window.matchMedia('(min-width: 750px)');
const ONNI_ECHTES_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');

/** Maus-Desktop: Hover oeffnet, Klick folgt dem Link. */
const onniHoverModus = () => ONNI_GROSSE_LEISTE.matches && ONNI_ECHTES_HOVER.matches;

/** iPad und Co: grosse Leiste, aber Finger statt Zeiger. Tipp oeffnet das Flyout. */
const onniTippModus = () => ONNI_GROSSE_LEISTE.matches && !ONNI_ECHTES_HOVER.matches;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * ONNI 01.08.2026: Riegel. Auf Desktop darf das Flyout NUR ueber unseren
   * Klick-Weg aufgehen. Messung im echten Browser hat gezeigt, dass es noch
   * einen zweiten Oeffnungsweg gibt, der schon beim Fokussieren zuschlaegt —
   * das Flyout ging vor dem click-Ereignis auf, und der eigene Klick hat es
   * danach wieder zugemacht. Genau das sah man als "erst der zweite Klick geht".
   * Statt diesen Weg weiter zu suchen, sperren wir alles ausser dem Klick.
   * @type {boolean}
   */
  #onniVonKlick = false;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('click', this.#onniAussenklick, true);
    document.addEventListener('keydown', this.#onniEscape);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('click', this.#onniAussenklick, true);
    document.removeEventListener('keydown', this.#onniEscape);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // ONNI: Im Tipp-Modus (iPad) oeffnet ausschliesslich unser Tipp-Weg. Jeder
    // andere Aufruf — Fokus, Zeiger, fremdes Skript — wird abgewiesen, sonst geht
    // das Flyout vor dem click-Ereignis auf und der eigene Tipp macht es wieder
    // zu: "der erste Tipp tut nichts".
    // Im Hover-Modus (Maus) ist genau das Gegenteil gewollt: Zeiger und Fokus
    // duerfen oeffnen.
    if (onniTippModus() && !this.#onniVonKlick) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * ONNI: Klick auf einen Menuepunkt oeffnet oder schliesst sein Untermenue,
   * statt dem Link zu folgen. Punkte ohne Untermenue bleiben normale Links.
   * @param {PointerEvent} event
   */
  klick = (event) => {
    // Maus-Desktop: Finger weg, der Link soll ganz normal auf die Seite fuehren.
    // Handy: laeuft ueber den Drawer, nicht ueber diese Leiste.
    if (!onniTippModus()) return;

    // Wahrheit ist der sichtbare Zustand, nicht das Gedaechtnis des Bauteils.
    // Steht intern noch ein aktiver Punkt, waehrend das Flyout laengst zu ist,
    // wuerde der naechste Klick nur "schliessen" — der Nutzer sieht dann, dass
    // der erste Klick nichts tut. Also vorher aufraeumen.
    const sichtbarOffen =
      (this.headerComponent?.style.getPropertyValue('--submenu-height') || '0px') !== '0px';
    if (!sichtbarOffen && this.#state.activeItem) {
      const alt = this.#state.activeItem;
      this.#state.activeItem = null;
      alt.ariaExpanded = 'false';
      this.ariaExpanded = 'false';
    }

    const item = findMenuItem(event.target);
    if (!item) return;

    // Kein Flyout an diesem Punkt -> ganz normal der Verlinkung folgen.
    const submenu = findSubmenu(item);
    if (!submenu) return;

    // WICHTIG: Das Untermenue liegt INNERHALB desselben <li>, an dem dieser
    // Handler haengt. Ein Klick auf einen Link im Flyout landet deshalb ebenfalls
    // hier. Der delegierte Listener ersetzt event.target durch das <li>, das
    // echte Ziel steht aber weiter in composedPath(). Liegt es im Untermenue,
    // halten wir uns komplett raus: das ist ein normaler Link.
    const echtesZiel = typeof event.composedPath === 'function' ? event.composedPath()[0] : null;
    if (echtesZiel instanceof Node && submenu.contains(echtesZiel)) return;

    event.preventDefault();

    if (this.#state.activeItem === item) {
      this.#deactivate(item);
      return;
    }

    if (this.#state.activeItem) this.#deactivate(this.#state.activeItem);

    this.#onniVonKlick = true;
    try {
      this.activate(event);
    } finally {
      this.#onniVonKlick = false;
    }
  };

  /** ONNI: Klick irgendwo ausserhalb der Menueleiste schliesst das Flyout. */
  #onniAussenklick = (event) => {
    if (!ONNI_GROSSE_LEISTE.matches || !this.#state.activeItem) return;
    const ziel = event.target;
    if (ziel instanceof Node && this.contains(ziel)) return;
    this.#deactivate();
  };

  /** ONNI: Escape schliesst das Flyout. */
  #onniEscape = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    this.#deactivate();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // ONNI: Im Tipp-Modus bleibt das Flyout offen, bis daneben getippt oder der
    // Punkt erneut getippt wird. Ein pointerleave direkt nach dem Tipp wuerde es
    // sonst sofort wieder zumachen.
    if (onniTippModus()) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
